import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Clock, Share2, ChevronDown, Loader2, ArrowRight } from 'lucide-react';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import { getCategoryColor } from '../constants/categoryColors';
import { getArticleSnippet } from '../utils/articleHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 7;

function CategorySection({ category, categorySlug, imageSrc, searchQuery }) {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const shareUrl = window.location.href;

  useEffect(() => {
    // Reset and refetch when category or search changes
    setSkip(0);
    setArticles([]);
    fetchInitial();
  }, [categorySlug, searchQuery]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const params = {
        category: categorySlug,
        skip: 0,
        limit: PAGE_SIZE
      };
      if (searchQuery) params.search = searchQuery;

      const [articlesRes, countRes] = await Promise.all([
        axios.get(`${API}/articles`, { params }),
        axios.get(`${API}/articles/count`, {
          params: {
            category: categorySlug,
            ...(searchQuery ? { search: searchQuery } : {})
          }
        })
      ]);

      setArticles(articlesRes.data);
      setTotal(countRes.data.count);
      setSkip(articlesRes.data.length);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const params = {
        category: categorySlug,
        skip: skip,
        limit: PAGE_SIZE
      };
      if (searchQuery) params.search = searchQuery;

      const { data } = await axios.get(`${API}/articles`, { params });
      setArticles(prev => [...prev, ...data]);
      setSkip(prev => prev + data.length);
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Hide entire section if no articles in this category
  if (!loading && articles.length === 0) return null;

  const hasMore = articles.length < total;
  const sectionId = categorySlug;
  const color = getCategoryColor(categorySlug);

  return (
    <section
      id={sectionId}
      className="py-12 border-t border-[#E5E5E5]"
      data-testid={`category-section-${sectionId}`}
    >
      <div className="flex items-center justify-between gap-6 mb-8 flex-wrap">
        <div className="flex items-center gap-6">
          <div
            className="w-20 h-20 overflow-hidden border-4"
            style={{ borderColor: color.bg }}
          >
            <img src={imageSrc} alt={category} className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col gap-2">
            <span
              className="inline-block px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase w-fit"
              style={{ backgroundColor: color.bg, color: color.text }}
            >
              CATEGORY
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-none font-bold text-[#1A1A2E]">
              {category.toUpperCase()}
            </h2>
          </div>
        </div>
        {total > 0 && (
          <div className="flex items-center gap-3" data-testid={`category-${sectionId}-meta`}>
            <div
              data-testid={`category-${sectionId}-count`}
              className="text-xs tracking-[0.2em] uppercase font-bold px-3 py-2"
              style={{ backgroundColor: color.tint, color: color.bg }}
            >
              SHOWING {articles.length} OF {total}
            </div>
            <Link
              to={`/category/${categorySlug}`}
              data-testid={`category-${sectionId}-view-all`}
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-bold px-3 py-2 border-2 transition-colors hover:text-white"
              style={{ borderColor: color.bg, color: color.bg }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              VIEW ALL
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>

      <div className="space-y-0">
        {articles.map((article, idx) => (
          <div
            key={article.id}
            data-testid={`article-${sectionId}-${idx}`}
            className="border-b border-[#E5E5E5] py-6 transition-colors cursor-pointer relative"
            style={{ '--hover-bg': color.tint }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color.tint; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <div className="grid grid-cols-12 gap-4">
              <Link
                to={`/article/${article.id}`}
                data-testid={`article-image-link-${sectionId}-${idx}`}
                className="col-span-12 sm:col-span-4 block"
              >
                <div className="relative h-32 overflow-hidden border border-[#E5E5E5] group">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Link>
              <div className="col-span-12 sm:col-span-8">
                <Link
                  to={`/article/${article.id}`}
                  data-testid={`article-title-link-${sectionId}-${idx}`}
                  className="block"
                >
                  <div className="flex items-center gap-2 mb-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A]" >
                    <Clock size={12} />
                    <span>{new Date(article.published_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{article.author}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl tracking-tight font-semibold text-[#0A0A0A] mb-2 hover:text-[#E63946] transition-colors" >
                    {article.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#525252] mb-3" >
                    {getArticleSnippet(article.content, 110)}
                  </p>
                </Link>
                <button
                  onClick={() => setShowShare(showShare === article.id ? null : article.id)}
                  data-testid={`share-button-${sectionId}-${idx}`}
                  className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] hover:text-[#E63946] transition-colors"
                  
                >
                  <Share2 size={14} />
                  SHARE
                </button>
                {showShare === article.id && (
                  <div className="flex items-center gap-3 mt-3" data-testid={`share-buttons-${sectionId}-${idx}`}>
                    <FacebookShareButton url={shareUrl} quote={article.title}>
                      <div className="px-3 py-1 bg-[#1877F2] text-white text-xs font-bold hover:opacity-80">Facebook</div>
                    </FacebookShareButton>
                    <TwitterShareButton url={shareUrl} title={article.title}>
                      <div className="px-3 py-1 bg-[#1DA1F2] text-white text-xs font-bold hover:opacity-80">Twitter</div>
                    </TwitterShareButton>
                    <WhatsappShareButton url={shareUrl} title={article.title}>
                      <div className="px-3 py-1 bg-[#25D366] text-white text-xs font-bold hover:opacity-80">WhatsApp</div>
                    </WhatsappShareButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            data-testid={`load-more-${sectionId}`}
            style={{ borderColor: color.bg, color: color.bg }}
            className="flex items-center gap-2 px-8 py-3 border-2 font-bold text-sm tracking-[0.2em] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:text-white"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color.bg; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {loadingMore ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                LOADING...
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                LOAD MORE
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
}

export default CategorySection;
